import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  CheckCircle2,
  ShoppingBag,
  Phone,
  Pizza as PizzaIcon,
  Beef,
  Sandwich,
  Flame,
  UtensilsCrossed,
  Leaf,
  Coffee,
  IceCream,
  Tag,
} from "lucide-react";
import { useCart, formatEUR } from "@/lib/cartStore";
import { RESTAURANT } from "@/data/catalog";

const SHORTCUTS = [
  { to: "/menu#pizza", label: "Pizza", icon: PizzaIcon },
  { to: "/menu#burgers", label: "Burgers", icon: Beef },
  { to: "/menu#naan", label: "Naan", icon: Sandwich },
  { to: "/menu#merguez", label: "Merguez", icon: Flame },
  { to: "/menu#starters", label: "Starters", icon: UtensilsCrossed },
  { to: "/menu#accompagnements", label: "Accompagnements", icon: Leaf },
  { to: "/menu#boissons", label: "Boissons", icon: Coffee },
  { to: "/menu#desserts", label: "Desserts", icon: IceCream },
  { to: "/offres", label: "Offres", icon: Tag },
];

/**
 * Panneau "Ajouté au récapitulatif" — apparaît automatiquement après chaque
 * ajout. Propose 3 actions principales + raccourcis vers les sections de la
 * carte pour ne jamais bloquer le client.
 */
export const PostAddPanel = () => {
  const { lastAdd, count, total, openDrawer } = useCart();
  const [open, setOpen] = useState(false);
  const [tsShown, setTsShown] = useState(null);
  const navigate = useNavigate();

  // Ouvre le panneau dès qu'une nouvelle addition se produit
  useEffect(() => {
    if (lastAdd && lastAdd.ts !== tsShown) {
      setOpen(true);
      setTsShown(lastAdd.ts);
    }
  }, [lastAdd, tsShown]);

  const close = () => setOpen(false);

  const handleSeeRecap = () => {
    close();
    // Petit délai pour fermer le dialog avant d'ouvrir le drawer
    setTimeout(() => openDrawer(), 50);
  };

  const handleShortcut = (to) => {
    close();
    setTimeout(() => navigate(to), 50);
  };

  if (!lastAdd) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="post-add-panel"
        className="bg-[#0F0F0F] border border-[#262626] text-white max-w-md p-0 overflow-hidden"
      >
        <DialogHeader className="px-5 pt-5 pb-3 text-left">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#22C55E] font-street">
            <CheckCircle2 size={13} strokeWidth={2.6} />
            Ajouté au récapitulatif
          </div>
          <DialogTitle className="font-street text-2xl tracking-wide leading-snug mt-1 text-white">
            {lastAdd.name}
          </DialogTitle>
          <DialogDescription className="text-[11px] text-white/55 leading-snug">
            {count} article{count > 1 ? "s" : ""} · Total estimé{" "}
            <span className="font-display text-[#FF9500]">{formatEUR(total)}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="px-5 pb-5 space-y-3">
          {/* Actions principales */}
          <div className="grid gap-2">
            <button
              type="button"
              onClick={handleSeeRecap}
              data-testid="post-add-see-recap"
              className="font-street w-full inline-flex items-center justify-center gap-2 rounded-md bg-[#FF9500] px-4 py-3 text-sm tracking-wider text-black shadow-[0_4px_0_#7a4900] hover:-translate-y-0.5 transition-all"
            >
              <ShoppingBag size={15} strokeWidth={2.6} />
              Voir mon récapitulatif
            </button>
            <a
              href={RESTAURANT.phoneTel}
              data-testid="post-add-call-cta"
              onClick={close}
              className="font-street w-full inline-flex items-center justify-center gap-2 rounded-md border border-[#FF9500]/40 bg-[#FF9500]/5 px-4 py-3 text-sm tracking-wider text-[#FF9500] hover:bg-[#FF9500]/15 transition-colors"
            >
              <Phone size={15} strokeWidth={2.6} />
              Appeler pour commander
            </a>
          </div>

          {/* Ajouter autre chose — shortcuts */}
          <div className="pt-2">
            <div className="text-[10px] uppercase tracking-[0.28em] text-white/45 font-street mb-2">
              Ajouter autre chose
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              {SHORTCUTS.map((s) => (
                <button
                  key={s.to}
                  type="button"
                  onClick={() => handleShortcut(s.to)}
                  data-testid={`post-add-shortcut-${s.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                  className="group flex items-center gap-1.5 rounded-md border border-[#262626] bg-[#0A0A0A] px-1.5 py-2 hover:border-[#FF9500]/50 hover:bg-[#FF9500]/5 transition-colors min-w-0"
                >
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-sm bg-[#FF9500]/10 text-[#FF9500] shrink-0">
                    <s.icon size={12} strokeWidth={2.4} />
                  </span>
                  <span className="font-street text-[9.5px] sm:text-[10px] uppercase tracking-wider text-white/85 leading-tight min-w-0 break-words">
                    {s.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <button
            type="button"
            onClick={close}
            data-testid="post-add-dismiss"
            className="w-full text-center text-[11px] uppercase tracking-[0.22em] text-white/45 hover:text-white/75 font-street pt-1"
          >
            Continuer ma navigation
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PostAddPanel;
